import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { api, ApiError } from "../api";
import { AuthError, AuthField, AuthShell } from "../components/AuthShell";

/** /reset asks for a link; /reset#<token> sets the new password. */
export default function ResetPassword() {
  const token = decodeURIComponent(window.location.hash.replace(/^#/, ""));
  return token ? <ChooseNew token={token} /> : <RequestLink />;
}

function RequestLink() {
  const [login, setLogin] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      await api.passwordReset.request(login.trim());
      setSent(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not send the link.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle={
        sent
          ? "If that account has an email on file, a reset link is on its way. It works for one hour."
          : "Enter your username or email. We email a link to the address on file."
      }
      footer={
        <>
          <Link to="/login" className="font-medium text-brand-fuchsia hover:underline">
            Back to sign in
          </Link>
          <p className="text-xs">No email on file? Ask an owner of your account to set a new password.</p>
        </>
      }
    >
      {!sent && (
        <form onSubmit={onSubmit} className="space-y-4">
          <AuthField
            label="Username or email"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
          <AuthError message={err} />
          <button className="bf-button w-full py-2.5" disabled={loading} type="submit">
            {loading ? "Sending..." : "Email me a link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

function ChooseNew({ token }: { token: string }) {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setErr("The two passwords do not match.");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await api.passwordReset.confirm(token, password);
      nav({ to: "/login" });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not set the password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
          label="New password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={10}
          autoComplete="new-password"
          hint="At least 10 characters."
          autoFocus
          required
        />
        <AuthField
          label="Repeat it"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          minLength={10}
          autoComplete="new-password"
          required
        />
        <AuthError message={err} />
        <button className="bf-button w-full py-2.5" disabled={loading} type="submit">
          {loading ? "Saving..." : "Set password"}
        </button>
      </form>
    </AuthShell>
  );
}
